import { PopoverUpdateBalanceComponent } from '@1bill-app/components/popover/popover-update-balance/popover-update-balance.component';
import { ROUTE_HOMEPAGE, ROUTE_CASH_SCREEN } from '@1bill-app/constants';
import { YodleeQuery } from '@1bill-app/services/yodlee/yodlee.query';
import { YodleeService } from '@1bill-app/services/yodlee/yodlee.service';
import { YodleeStore } from '@1bill-app/services/yodlee/yodlee.store';
import {
  YodleeAccount,
  AccountHistoricalBalance,
  AccountHistoryItem,
  CashChartResponse,
} from '@1bill-app/services/yodlee/yodlee.types';
import { ChangeDetectorRef, Component, OnDestroy, ViewChild } from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { PopoverController } from '@ionic/angular';
import dayjs from 'dayjs';
import _ from 'lodash';
import { NGXLogger } from 'ngx-logger';
import { SubSink } from 'subsink';
import { HomeService } from './../../../services/home/home.service';
import { getLastOfInterval } from '@1bill-app/helpers/date.helpers';
import { CashChartComponent } from '@1bill-app/components/cash-chart/cash-chart.component';
import { tap } from 'rxjs/operators';

@Component({
  selector: 'app-account-view',
  templateUrl: './account-view.page.html',
  styleUrls: ['./account-view.page.scss'],
})
export class AccountViewPage implements OnDestroy {
  @ViewChild('cashChart') cashChart: CashChartComponent;

  constructor(
    private homeService: HomeService,
    private yodleeQuery: YodleeQuery,
    private yodleeStore: YodleeStore,
    private logger: NGXLogger,
    private yodleeService: YodleeService,
    private router: Router,
    private route: ActivatedRoute,
    private popoverController: PopoverController,
    private cdRef: ChangeDetectorRef,
  ) {}

  public homeDataEvent$ = this.homeService.homeDataEvent$;
  public isPullToRefresh: boolean;

  subs = new SubSink();
  id: number;
  title: string;
  // public loading$ = this.yodleeQuery.isLoading$;
  accountDetails$ = this.yodleeQuery.accountDetails$;
  accountDetails: YodleeAccount;
  accountsHistory: AccountHistoryItem[] = [];
  accountHistoricalBalances: AccountHistoricalBalance[];
  availablePeriods: string[];

  date = dayjs().format('YYYY-MM-DD');
  interval: 'M' | 'W' | 'Y' = 'M';

  get subtitle() {
    if (!this.accountDetails) return '';
    else if (this.accountDetails?.isManualAccount) return this.accountDetails.accountType;
    return 'Acc. Number: ' + this.accountDetails.accountNumber;
  }

  get defaultHref() {
    if (this.route.snapshot.queryParams?.s === 'paymentOptions') {
      return;
    }
    if (this.accountDetails) {
      return `${ROUTE_CASH_SCREEN}?type=${this.accountDetails.type}`;
    }
    return ROUTE_HOMEPAGE;
  }

  public accountViewChartData$ = this.yodleeQuery.accountViewChartData$.pipe(
    tap((cd) => {
      this.logger.log('accountViewChartData$ updated:', cd);
    }),
  );
  currentDurationDate$ = this.yodleeQuery.accountViewCurrentDurationDate$;

  private subscriptionHandler = (accountViewData: {
    cashChartData: CashChartResponse;
    accountBalances: AccountHistoricalBalance[];
  }) => {
    this.logger.log('Updated chart data:', accountViewData);
    this.availablePeriods = accountViewData.cashChartData.chartData
      .map((cd) => cd.date)
      .sort((a, b) => (a > b ? -1 : 1));
    // Force balance activity items to re-initialise
    this.accountHistoricalBalances = [];
    setTimeout(() => {
      this.accountHistoricalBalances = accountViewData.accountBalances;
    }, 0);
  };

  async ionViewWillEnter() {
    this.id = Number(this.route.snapshot.queryParams?.id);
    if (!this.id) {
      this.router.navigateByUrl(ROUTE_HOMEPAGE);
      return;
    }

    // this.subs.sink = this.router.events.subscribe((event) => {
    //   if (event instanceof NavigationEnd) {
    //     this.refreshData();
    //   }
    // });

    this.subs.sink = this.yodleeService.getAccounts().subscribe();
    this.yodleeStore.update({ currentAccountId: this.id });
    this.subs.sink = this.accountDetails$.subscribe((accountDetails) => {
      this.accountDetails = accountDetails;
      if (!!accountDetails?.id) {
        this.reflowChart();
      }
    });

    this.fetchChartData().subscribe(this.subscriptionHandler);
  }

  async ionViewWillLeave() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    this.yodleeStore.update({ accountViewChartData: null });
    this.cashChart.showLoading();
    this.subs.unsubscribe();
  }

  async navToSettings() {
    await this.router.navigate(['/cash-screen/account-view/payment-settings', this.id], {
      relativeTo: this.route,
      skipLocationChange: true,
    });
  }
  async refreshData() {
    this.subs.sink = this.yodleeService.getAccounts().subscribe();
    this.yodleeStore.update({ currentAccountId: this.id });
    this.subs.sink = this.accountDetails$.subscribe((accountDetails) => {
      this.accountDetails = accountDetails;
    });
    
    this.fetchChartData().subscribe(this.subscriptionHandler);
  }

  fetchChartData() {
    return this.yodleeService.getAccountViewData(
      this.date,
      this.interval,
      this.id.toString(),
      '' ?? this.route.snapshot.queryParamMap.get('id'),
    );
  }

  async presentUpdateBalance(event) {
    const popover = await this.popoverController.create({
      component: PopoverUpdateBalanceComponent,
      mode: 'ios',
    });
    await popover.present();
    popover.onDidDismiss().then((updated) => {
      if (updated) {
        this.refreshData();
      }
    });
  }

  handleLinkBankAccount() {
    this.homeService.presentLinkBankAccountModal();
  }

  trackByFn(balanceItem: AccountHistoricalBalance) {
    return balanceItem?.accountId;
  }

  doRefresh(event) {
    this.isPullToRefresh = true;
    this.fetchChartData()
      .toPromise()
      .then(this.subscriptionHandler)
      .then(() => {
        this.isPullToRefresh = false;
      })
      .catch((err) => {
        this.logger.error(err);
      })
      .finally(() => {
        event.target.complete();
      });
  }

  showListMonthLabel(index: number) {
    if (index == 0) {
      return true;
    } else {
      let thisBalanceItem = dayjs(this.accountHistoricalBalances[index].asOfDate)
        .format('MMM')
        .toUpperCase();
      let lastBalanceItem = dayjs(this.accountHistoricalBalances[index - 1].asOfDate)
        .format('MMM')
        .toUpperCase();
      if (thisBalanceItem != lastBalanceItem) {
        return true;
      }
    }
    return false;
  }

  getMonthText(index: number) {
    return dayjs(this.accountHistoricalBalances[index].asOfDate).format('MMMM');
  }

  intervalChanged(to: 'M' | 'W' | 'Y') {
    this.interval = to;
    this.date = getLastOfInterval(to);
    this.fetchChartData().subscribe(this.subscriptionHandler);
  }

  durationChanged(to: string) {
    this.date = to;
    this.fetchChartData().subscribe(this.subscriptionHandler);
  }

  /**
   * Reflows chart asynchronously so that it is not impacted by the
   * synchronous internal loading of the chart component itself.
   */
  async reflowChart() {
    setTimeout(() => {
      this.cashChart.reflow();
    }, 0);
  }

  /**
   * For each balance item, get date period (i.e. monthly/quarterly/yearly)
   * and balance.amount (Gets the final balance.amount for that period);
   * Add date period and balance.amount to map as key and value respectively;
   * This function can only group balances by a monthly basis.
   */
  groupDataByPeriod() {
    let balMap = new Map<string, number>();

    this.accountHistoricalBalances.forEach((balanceItem) => {
      let date = dayjs(balanceItem.asOfDate).format('MMM');
      let day = dayjs(balanceItem.asOfDate).format('DD');
      let month = dayjs(balanceItem.asOfDate).format('MMM').toUpperCase();

      balMap.set(month, balanceItem.balance.amount);
    });

    let string = 'TOTAL BALANCE PER MONTH (cash-screen chart): ';
    for (let [key, value] of balMap.entries()) {
      string += '\n' + key.toString() + ': ' + value.toString();
    }
    this.logger.log(string);
    return balMap;
  }
}
